/* ------------------------------------------------------------------
   Collector's Edge card centering tool (sections/ce-centering-tool.liquid)
   Rebuilt from scratch, September 2026.

   How it works
   - The photo is decoded in the browser, scaled to at most 2400px on the
     long edge and kept in a canvas. Nothing is uploaded anywhere.
   - Eight guide lines sit on the photo: four on the card edges (orange) and
     four on the inside of the printed border (blue). Border widths are the
     gaps between them, so the ratios are pure geometry.
   - "Work" coordinates are pixels of that canvas. The image can be turned
     (quarter turns rebuild the canvas; fine straightening rotates it about
     its centre at draw time) while the lines stay axis aligned.
   - Auto-detect: straighten by maximising edge-projection sharpness, then
     pick the card rectangle from projection peaks (card aspect ratio as a
     constraint), then the first strong edge inward on each side is the
     border line. Always adjustable by hand.
   - Input: mouse, touch and pen through Pointer Events, pinch and ctrl+wheel
     zoom, a loupe while dragging, keyboard sliders on every handle, and
     hold-to-repeat nudge buttons for precise finger work.

   Store is light-only; the dark stage is fixed styling, never a
   prefers-color-scheme rule.
   ------------------------------------------------------------------ */
(() => {
  'use strict';

  const root = document.querySelector('[data-cc]');
  if (!root || root.dataset.ccReady) return;
  root.dataset.ccReady = '1';
  root.classList.add('cc--js');

  const $ = (sel, el = root) => el.querySelector(sel);
  const $$ = (sel, el = root) => Array.from(el.querySelectorAll(sel));

  const GRADERS = JSON.parse($('[data-cc-graders]').textContent);
  const MAX_SIDE = 2400;
  const KEYS = ['ol', 'il', 'ir', 'or', 'ot', 'it', 'ib', 'ob'];
  const META = {
    ol: { axis: 'x', kind: 'outer', name: 'Left card edge' },
    or: { axis: 'x', kind: 'outer', name: 'Right card edge' },
    ot: { axis: 'y', kind: 'outer', name: 'Top card edge' },
    ob: { axis: 'y', kind: 'outer', name: 'Bottom card edge' },
    il: { axis: 'x', kind: 'inner', name: 'Left border line' },
    ir: { axis: 'x', kind: 'inner', name: 'Right border line' },
    it: { axis: 'y', kind: 'inner', name: 'Top border line' },
    ib: { axis: 'y', kind: 'inner', name: 'Bottom border line' },
  };
  const COL = { outer: '#ff6a2b', inner: '#35d3ff', bandX: 'rgba(255,106,43,0.2)', bandY: 'rgba(53,211,255,0.18)' };
  const SIDES = { front: 'Front', back: 'Back' };

  /* ---------- elements ---------- */
  const el = {
    ws: $('[data-cc-ws]'),
    host: $('[data-cc-ws-host]'),
    dialog: $('[data-cc-dialog]'),
    stage: $('[data-cc-stage]'),
    canvas: $('[data-cc-canvas]'),
    loupe: $('[data-cc-loupe]'),
    handles: $('[data-cc-handles]'),
    empty: $('[data-cc-empty]'),
    busy: $('[data-cc-busy]'),
    toast: $('[data-cc-toast]'),
    file: $('[data-cc-file]'),
    capture: $('[data-cc-capture]'),
    angle: $('[data-cc-angle]'),
    angleVal: $('[data-cc-angle-val]'),
    sel: $('[data-cc-sel]'),
    size: $('[data-cc-size]'),
    status: $('[data-cc-status]'),
    results: $('[data-cc-results]'),
    grades: $('[data-cc-grades]'),
    summary: $('[data-cc-summary]'),
    cam: $('[data-cc-cam]'),
    video: $('[data-cc-video]'),
    camMsg: $('[data-cc-cam-msg]'),
    drop: $('[data-cc-drop]'),
  };
  const ctx = el.canvas.getContext('2d');
  const lctx = el.loupe.getContext('2d');

  /* ---------- state ---------- */
  // guide: where the camera guide frame was, in photo pixels (camera photos only)
  const newSide = () => ({ base: null, angle: 0, lines: null, source: '', guide: null });
  const S = {
    side: 'front',
    sides: { front: newSide(), back: newSide() },
    size: [63, 88],
    sel: 'ol',
    view: { s: 1, tx: 0, ty: 0, fitted: true },
    W: 0, H: 0, dpr: 1,
    uploadSide: 'front',
  };
  const cur = () => S.sides[S.side];

  /* ---------- small helpers ---------- */
  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
  const f1 = (v) => (Math.round(v * 10) / 10).toFixed(1);
  const ratio = (big) => `${f1(big)}/${f1(100 - big)}`;
  const limit = (big) => { const a = Math.round(big * 10) / 10; return `${a}/${Math.round((100 - a) * 10) / 10}`; };
  const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
  let statusTimer = 0;
  function announce(msg) {
    clearTimeout(statusTimer);
    statusTimer = setTimeout(() => { el.status.textContent = msg; }, 60);
  }
  let toastTimer = 0;
  function toast(msg, ms = 2600) {
    el.toast.textContent = msg;
    el.toast.hidden = false;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => { el.toast.hidden = true; }, ms);
  }
  const nextFrame = () => new Promise((r) => requestAnimationFrame(() => r()));

  /* ==================================================================
     Image intake
     ================================================================== */
  async function decode(blob) {
    if ('createImageBitmap' in window) {
      try { return await createImageBitmap(blob, { imageOrientation: 'from-image' }); } catch (e) { /* fall through */ }
    }
    const url = URL.createObjectURL(blob);
    try {
      const img = new Image();
      img.decoding = 'async';
      img.src = url;
      await img.decode();
      return img;
    } finally {
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    }
  }

  function toBase(src) {
    const w = src.naturalWidth || src.videoWidth || src.width;
    const h = src.naturalHeight || src.videoHeight || src.height;
    const k = Math.min(1, MAX_SIDE / Math.max(w, h));
    const c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(w * k));
    c.height = Math.max(1, Math.round(h * k));
    const x = c.getContext('2d');
    x.imageSmoothingQuality = 'high';
    x.drawImage(src, 0, 0, c.width, c.height);
    if (typeof src.close === 'function') src.close();
    return c;
  }

  async function loadBlob(blob, label) {
    if (!blob) return;
    if (blob.type && !/^image\//.test(blob.type)) {
      toast('That file is not an image. Try a JPG, PNG or HEIC photo.');
      return;
    }
    showBusy('Loading photo');
    try {
      const base = toBase(await decode(blob));
      await setImage(base, label || blob.name || 'Photo');
    } catch (err) {
      hideBusy();
      toast("That photo couldn't be opened. If it's HEIC, try a screenshot or JPG.", 4000);
    }
  }

  async function setImage(base, label, guide) {
    const side = S.uploadSide;
    const sd = S.sides[side];
    sd.base = base;
    sd.angle = 0;
    sd.source = label;
    sd.guide = guide || null;
    sd.lines = sd.guide ? guideLines(sd.guide) : defaultLines(base);
    setSide(side, true);
    openWs();
    await nextFrame();
    fit();
    draw();
    await autoDetect(true);
  }

  function defaultLines(c) {
    const W = c.width, H = c.height, asp = S.size[0] / S.size[1];
    let ch = H * 0.8, cw = ch * asp;
    if (cw > W * 0.8) { cw = W * 0.8; ch = cw / asp; }
    const x0 = (W - cw) / 2, y0 = (H - ch) / 2, bx = cw * 0.055, by = ch * 0.045;
    return { ol: x0, or: x0 + cw, ot: y0, ob: y0 + ch, il: x0 + bx, ir: x0 + cw - bx, it: y0 + by, ib: y0 + ch - by };
  }

  // starting lines on the camera guide frame: the card was lined up there
  function guideLines(g) {
    const cw = g.x1 - g.x0, ch = g.y1 - g.y0, bx = cw * 0.05, by = ch * 0.035;
    return { ol: g.x0, or: g.x1, ot: g.y0, ob: g.y1, il: g.x0 + bx, ir: g.x1 - bx, it: g.y0 + by, ib: g.y1 - by };
  }

  // does a detected card look like the card the user lined up in the guide frame?
  function matchesGuide(L, g) {
    const gw = g.x1 - g.x0, gh = g.y1 - g.y0, cw = L.or - L.ol, ch = L.ob - L.ot;
    const cx = (L.ol + L.or) / 2, cy = (L.ot + L.ob) / 2;
    return cw > gw * 0.55 && cw < gw * 1.7 && ch > gh * 0.55 && ch < gh * 1.7 &&
      Math.abs(cx - (g.x0 + g.x1) / 2) < gw * 0.45 && Math.abs(cy - (g.y0 + g.y1) / 2) < gh * 0.45;
  }

  function rotateBase(c, dir) {
    const n = document.createElement('canvas');
    n.width = c.height;
    n.height = c.width;
    const x = n.getContext('2d');
    x.translate(n.width / 2, n.height / 2);
    x.rotate((dir * Math.PI) / 2);
    x.drawImage(c, -c.width / 2, -c.height / 2);
    return n;
  }

  /* A sample card, drawn here so there's no third-party artwork involved. */
  function sampleCard(back) {
    const W = 1500, H = 1900;
    const c = document.createElement('canvas');
    c.width = W; c.height = H;
    const x = c.getContext('2d');
    const bg = x.createLinearGradient(0, 0, W, H);
    bg.addColorStop(0, '#2b3038'); bg.addColorStop(1, '#171a1f');
    x.fillStyle = bg; x.fillRect(0, 0, W, H);
    for (let i = 0; i < 2600; i++) {           // a little texture so it reads as a surface
      x.fillStyle = `rgba(255,255,255,${Math.random() * 0.035})`;
      x.fillRect(Math.random() * W, Math.random() * H, 2, 2);
    }
    const cw = 1008, chh = 1408, u = cw / 63;   // 63 x 88 mm card, u = px per mm
    x.save();
    x.translate(W / 2 + 14, H / 2 - 10);
    x.rotate((back ? 1.4 : -1.9) * Math.PI / 180);
    x.translate(-cw / 2, -chh / 2);
    x.shadowColor = 'rgba(0,0,0,0.55)'; x.shadowBlur = 40; x.shadowOffsetY = 18;
    const rr = (px, py, pw, ph, r) => { x.beginPath(); x.roundRect ? x.roundRect(px, py, pw, ph, r) : x.rect(px, py, pw, ph); };
    // borders in mm: front 3.3 / 2.3 left-right, 2.8 / 3.5 top-bottom; back 2.5 / 3.6, 2.6 / 3.4
    const b = back ? { l: 2.5, r: 3.6, t: 2.6, bt: 3.4 } : { l: 3.3, r: 2.3, t: 2.8, bt: 3.5 };
    rr(0, 0, cw, chh, 3 * u);
    x.fillStyle = back ? '#1f4fa3' : '#f1c232';
    x.fill();
    x.shadowColor = 'transparent';
    const ix = b.l * u, iy = b.t * u, iw = cw - (b.l + b.r) * u, ih = chh - (b.t + b.bt) * u;
    if (back) {
      const g = x.createRadialGradient(ix + iw / 2, iy + ih / 2, 20, ix + iw / 2, iy + ih / 2, ih * 0.62);
      g.addColorStop(0, '#9fd3ff'); g.addColorStop(0.35, '#3d7fd6'); g.addColorStop(1, '#13306e');
      rr(ix, iy, iw, ih, 1.4 * u); x.fillStyle = g; x.fill();
      x.save(); x.translate(ix + iw / 2, iy + ih / 2);
      for (let i = 0; i < 18; i++) {
        x.rotate(Math.PI / 9);
        x.beginPath(); x.moveTo(0, 0); x.quadraticCurveTo(iw * 0.18, ih * 0.1, iw * 0.05, ih * 0.46);
        x.strokeStyle = 'rgba(255,255,255,0.10)'; x.lineWidth = 16; x.stroke();
      }
      x.beginPath();
      for (let i = 0; i < 10; i++) {
        const r = i % 2 ? iw * 0.1 : iw * 0.24, a = (i * Math.PI) / 5 - Math.PI / 2;
        x.lineTo(Math.cos(a) * r, Math.sin(a) * r);
      }
      x.closePath(); x.fillStyle = '#ffd34d'; x.fill();
      x.restore();
    } else {
      rr(ix, iy, iw, ih, 1.2 * u); x.fillStyle = '#e8e1cf'; x.fill();
      x.fillStyle = '#2a2a2a'; x.font = `700 ${4.2 * u}px system-ui, sans-serif`;
      x.fillText('Sample Card', ix + 3 * u, iy + 6.2 * u);
      x.font = `700 ${3 * u}px system-ui, sans-serif`; x.textAlign = 'right';
      x.fillText('HP 120', ix + iw - 3 * u, iy + 6 * u); x.textAlign = 'left';
      const ax = ix + 2.6 * u, ay = iy + 8.4 * u, aw = iw - 5.2 * u, ah = 36 * u;
      const sky = x.createLinearGradient(0, ay, 0, ay + ah);
      sky.addColorStop(0, '#ffb86b'); sky.addColorStop(0.55, '#ff6f61'); sky.addColorStop(1, '#5b2a86');
      x.fillStyle = sky; x.fillRect(ax, ay, aw, ah);
      x.fillStyle = 'rgba(255,240,200,0.85)'; x.beginPath(); x.arc(ax + aw * 0.7, ay + ah * 0.38, 7 * u, 0, Math.PI * 2); x.fill();
      x.fillStyle = '#2c1a4d'; x.beginPath(); x.moveTo(ax, ay + ah);
      x.lineTo(ax + aw * 0.28, ay + ah * 0.52); x.lineTo(ax + aw * 0.5, ay + ah * 0.74); x.lineTo(ax + aw * 0.78, ay + ah * 0.45);
      x.lineTo(ax + aw, ay + ah * 0.7); x.lineTo(ax + aw, ay + ah); x.fill();
      x.strokeStyle = '#c9a227'; x.lineWidth = 0.6 * u; x.strokeRect(ax, ay, aw, ah);
      for (let i = 0; i < 6; i++) {
        x.fillStyle = i % 3 === 0 ? '#3a3a3a' : '#9a958a';
        x.fillRect(ix + 4 * u, ay + ah + 6 * u + i * 4.4 * u, (i % 3 === 0 ? 0.45 : 0.82) * (iw - 8 * u), 1.6 * u);
      }
    }
    // soft glare
    const gl = x.createLinearGradient(0, 0, cw, chh);
    gl.addColorStop(0.3, 'rgba(255,255,255,0)'); gl.addColorStop(0.45, 'rgba(255,255,255,0.10)'); gl.addColorStop(0.6, 'rgba(255,255,255,0)');
    rr(0, 0, cw, chh, 3 * u); x.fillStyle = gl; x.fill();
    x.restore();
    return c;
  }

  /* The front sample is a real card (Mega Charizard X ex, our own product image) laid on a
     desk-like background at a slight angle, so the demo shows auto-detect on a real print.
     Falls back to the drawn card if the image can't load. */
  async function samplePhoto(back) {
    const url = root.dataset.ccSample;
    if (back || !url) return sampleCard(back);
    try {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.src = url + (url.includes('?') ? '&' : '?') + 'width=1200';
      await img.decode();
      const W = 1500, H = 1900;
      const c = document.createElement('canvas');
      c.width = W; c.height = H;
      const x = c.getContext('2d');
      const bg = x.createLinearGradient(0, 0, W, H);
      bg.addColorStop(0, '#2b3038'); bg.addColorStop(1, '#171a1f');
      x.fillStyle = bg; x.fillRect(0, 0, W, H);
      for (let i = 0; i < 2600; i++) {
        x.fillStyle = `rgba(255,255,255,${Math.random() * 0.035})`;
        x.fillRect(Math.random() * W, Math.random() * H, 2, 2);
      }
      const ch = 1400, cw = ch * (img.naturalWidth / img.naturalHeight), r = cw * 0.045;
      x.save();
      x.translate(W / 2 + 12, H / 2 - 8);
      x.rotate((-1.8 * Math.PI) / 180);
      x.shadowColor = 'rgba(0,0,0,0.55)'; x.shadowBlur = 40; x.shadowOffsetY = 18;
      x.beginPath();
      if (x.roundRect) x.roundRect(-cw / 2, -ch / 2, cw, ch, r); else x.rect(-cw / 2, -ch / 2, cw, ch);
      x.fillStyle = '#888'; x.fill();
      x.shadowColor = 'transparent';
      x.clip();
      x.drawImage(img, -cw / 2, -ch / 2, cw, ch);
      x.restore();
      return c;
    } catch (err) {
      return sampleCard(back);
    }
  }

  /* ==================================================================
     View + drawing
     ================================================================== */
  function resizeCanvas() {
    const r = el.stage.getBoundingClientRect();
    if (!r.width || !r.height) return false;
    S.dpr = Math.min(window.devicePixelRatio || 1, 2.5);
    S.W = r.width; S.H = r.height;
    el.canvas.width = Math.round(r.width * S.dpr);
    el.canvas.height = Math.round(r.height * S.dpr);
    return true;
  }

  /* What "fit" frames. A phone photo where the card fills most of the frame shows the whole
     photo, so a wrongly detected edge can never hide part of the card off-screen. A small card
     on a big desk shot is framed on the card (plus room for handles). */
  function fitRect(sd) {
    const L = sd.lines, b = sd.base;
    const whole = { x0: 0, y0: 0, x1: b.width, y1: b.height };
    // camera photo: never frame tighter than the guide frame the card was lined up in
    if (sd.guide) {
      const g = sd.guide, mx = (g.x1 - g.x0) * 0.14, my = (g.y1 - g.y0) * 0.1;
      return {
        x0: Math.max(0, Math.min(g.x0 - mx, L.ol - mx)), y0: Math.max(0, Math.min(g.y0 - my, L.ot - my)),
        x1: Math.min(b.width, Math.max(g.x1 + mx, L.or + mx)), y1: Math.min(b.height, Math.max(g.y1 + my, L.ob + my)),
      };
    }
    const cardArea = (L.or - L.ol) * (L.ob - L.ot);
    if (cardArea / (b.width * b.height) > 0.22) return whole;
    const cx = (L.ol + L.or) / 2, cy = (L.ot + L.ob) / 2;
    // and never zoom in past ~40% of the photo, so a mis-detected small shape can't fill the screen
    const hw = Math.max(((L.or - L.ol) * 1.3) / 2, b.width * 0.2), hh = Math.max(((L.ob - L.ot) * 1.2) / 2, b.height * 0.2);
    return { x0: cx - hw, y0: cy - hh, x1: cx + hw, y1: cy + hh };
  }

  function fitScale(sd) {
    const r = fitRect(sd), pad = S.W < 560 ? 12 : 28;
    return Math.min((S.W - pad * 2) / (r.x1 - r.x0), (S.H - pad * 2) / (r.y1 - r.y0));
  }

  // zoom limits: out to a bit smaller than the whole photo, in to 14x the fitted view
  function zoomLimits(sd) {
    const pad = S.W < 560 ? 12 : 28;
    const whole = Math.min((S.W - pad * 2) / sd.base.width, (S.H - pad * 2) / sd.base.height);
    const base = fitScale(sd);
    return [Math.min(whole * 0.8, base * 0.6), base * 14];
  }

  function fit() {
    const sd = cur();
    if (!sd.base || !resizeCanvas()) return;
    const r = fitRect(sd);
    const s = fitScale(sd);
    S.view = { s, tx: S.W / 2 - ((r.x0 + r.x1) / 2) * s, ty: S.H / 2 - ((r.y0 + r.y1) / 2) * s, fitted: true };
    draw();
  }

  function zoomAt(factor, px, py) {
    const sd = cur();
    if (!sd.base) return;
    const v = S.view, [lo, hi] = zoomLimits(sd);
    const s2 = clamp(v.s * factor, lo, hi);
    const k = s2 / v.s;
    v.tx = px - (px - v.tx) * k;
    v.ty = py - (py - v.ty) * k;
    v.s = s2;
    v.fitted = false;
    draw();
  }

  function drawImageTo(c, sd, s, tx, ty) {
    const b = sd.base, cx = b.width / 2, cy = b.height / 2;
    c.save();
    c.translate(tx + cx * s, ty + cy * s);
    c.rotate((sd.angle * Math.PI) / 180);
    c.scale(s, s);
    c.imageSmoothingQuality = s < 1 ? 'high' : 'low';
    c.drawImage(b, -cx, -cy);
    c.restore();
  }

  function drawOverlay(c, L, s, tx, ty, o) {
    const X = (v) => tx + v * s, Y = (v) => ty + v * s;
    const ol = X(L.ol), or = X(L.or), ot = Y(L.ot), ob = Y(L.ob);
    const il = X(L.il), ir = X(L.ir), it = Y(L.it), ib = Y(L.ib);
    if (o.bands) {
      c.fillStyle = COL.bandX;
      c.fillRect(ol, it, il - ol, ib - it);
      c.fillRect(ir, it, or - ir, ib - it);
      c.fillStyle = COL.bandY;
      c.fillRect(il, ot, ir - il, it - ot);
      c.fillRect(il, ib, ir - il, ob - ib);
    }
    const lw = o.lw || 1.75;
    for (const key of KEYS) {
      const m = META[key];
      const p = m.axis === 'x' ? X(L[key]) : Y(L[key]);
      const col = COL[m.kind];
      const hot = o.sel === key;
      // faint full-length guide
      if (o.guides) {
        c.globalAlpha = hot ? 0.75 : 0.38;
        c.strokeStyle = col; c.lineWidth = 1;
        c.beginPath();
        if (m.axis === 'x') { c.moveTo(p, 0); c.lineTo(p, o.h); } else { c.moveTo(0, p); c.lineTo(o.w, p); }
        c.stroke();
        c.globalAlpha = 1;
      }
      const a0 = m.axis === 'x' ? ot : ol, a1 = m.axis === 'x' ? ob : or;
      c.beginPath();
      if (m.axis === 'x') { c.moveTo(p, a0); c.lineTo(p, a1); } else { c.moveTo(a0, p); c.lineTo(a1, p); }
      c.strokeStyle = 'rgba(0,0,0,0.6)'; c.lineWidth = (hot ? lw + 1 : lw) + 2; c.stroke();
      c.strokeStyle = col; c.lineWidth = hot ? lw + 1 : lw; c.stroke();
    }
    if (o.labels) {
      const mm = measure({ lines: L });
      c.font = '600 11px ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';
      c.textAlign = 'center'; c.textBaseline = 'middle';
      const tag = (txt, x, y, room) => {
        if (room < 30) return;
        const w = c.measureText(txt).width + 10;
        c.fillStyle = 'rgba(8,10,14,0.78)';
        c.beginPath(); c.roundRect ? c.roundRect(x - w / 2, y - 9, w, 18, 9) : c.rect(x - w / 2, y - 9, w, 18); c.fill();
        c.fillStyle = '#fff'; c.fillText(txt, x, y + 0.5);
      };
      const midY = (it + ib) / 2, midX = (il + ir) / 2;
      tag(f1(mm.lr.a), (ol + il) / 2, midY, il - ol);
      tag(f1(mm.lr.b), (ir + or) / 2, midY, or - ir);
      tag(f1(mm.tb.a), midX, (ot + it) / 2, (it - ot) * 2.2);
      tag(f1(mm.tb.b), midX, (ib + ob) / 2, (ob - ib) * 2.2);
    }
  }

  let raf = 0;
  function draw() {
    if (raf) return;
    raf = requestAnimationFrame(() => { raf = 0; paint(); });
  }

  function paint() {
    const sd = cur();
    const d = S.dpr;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, el.canvas.width, el.canvas.height);
    el.empty.hidden = !!sd.base;
    el.handles.hidden = !sd.base;
    root.classList.toggle('cc--has-image', !!sd.base);
    if (!sd.base) return;
    ctx.setTransform(d, 0, 0, d, 0, 0);
    const v = S.view;
    drawImageTo(ctx, sd, v.s, v.tx, v.ty);
    drawOverlay(ctx, sd.lines, v.s, v.tx, v.ty, { bands: true, guides: true, labels: true, sel: S.sel, w: S.W, h: S.H });
    placeHandles();
    updateReadout();
  }

  /* ---------- handles (focusable sliders) ---------- */
  const handleEls = {};
  function buildHandles() {
    for (const key of KEYS) {
      const m = META[key];
      const b = document.createElement('button');
      b.type = 'button';
      b.className = `cc-h cc-h--${m.kind} cc-h--${m.axis}`;
      b.dataset.key = key;
      b.setAttribute('role', 'slider');
      b.setAttribute('aria-label', m.name);
      b.setAttribute('aria-orientation', m.axis === 'x' ? 'horizontal' : 'vertical');
      b.innerHTML = m.axis === 'x'
        ? '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 7l-5 5 5 5M16 7l5 5-5 5M3 12h18"/></svg>'
        : '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 8l5-5 5 5M7 16l5 5 5-5M12 3v18"/></svg>';
      b.addEventListener('keydown', onHandleKey);
      b.addEventListener('focus', () => select(key));
      el.handles.appendChild(b);
      handleEls[key] = b;
    }
  }

  function handlePos(key) {
    const L = cur().lines, v = S.view, m = META[key];
    const X = (q) => v.tx + q * v.s, Y = (q) => v.ty + q * v.s;
    const t = m.kind === 'outer' ? 0.34 : 0.66;
    const edge = 24;
    if (m.axis === 'x') {
      const x = X(L[key]);
      const y = clamp(Y(L.ot) + (Y(L.ob) - Y(L.ot)) * t, edge, S.H - edge);
      return { x, y, vis: x > -8 && x < S.W + 8 };
    }
    const y = Y(L[key]);
    const x = clamp(X(L.ol) + (X(L.or) - X(L.ol)) * t, edge, S.W - edge);
    return { x, y, vis: y > -8 && y < S.H + 8 };
  }

  function placeHandles() {
    const sd = cur();
    for (const key of KEYS) {
      const b = handleEls[key], p = handlePos(key);
      b.style.transform = `translate(${p.x.toFixed(1)}px, ${p.y.toFixed(1)}px)`;
      b.hidden = !p.vis;
      b.classList.toggle('is-sel', S.sel === key);
      const axisMax = META[key].axis === 'x' ? sd.base.width : sd.base.height;
      b.setAttribute('aria-valuemin', '0');
      b.setAttribute('aria-valuemax', String(axisMax));
      b.setAttribute('aria-valuenow', String(Math.round(sd.lines[key])));
      b.setAttribute('aria-valuetext', `${META[key].name}, ${Math.round(sd.lines[key])} pixels`);
    }
  }

  function select(key) {
    if (!META[key]) return;
    S.sel = key;
    const m = META[key];
    el.sel.textContent = m.name;
    root.dataset.ccAxis = m.axis;
    draw();
  }

  /* Keep the eight lines in order: edge < border < border < edge on both axes. */
  function setLine(key, val) {
    const sd = cur(), L = sd.lines;
    const W = sd.base.width, H = sd.base.height, g = 1;
    const lim = {
      ol: [-W * 0.05, L.il - g], il: [L.ol + g, L.ir - g], ir: [L.il + g, L.or - g], or: [L.ir + g, W * 1.05],
      ot: [-H * 0.05, L.it - g], it: [L.ot + g, L.ib - g], ib: [L.it + g, L.ob - g], ob: [L.ib + g, H * 1.05],
    }[key];
    L[key] = clamp(val, lim[0], lim[1]);
  }

  function nudge(dir, step) {
    const sd = cur();
    if (!sd.base) return;
    setLine(S.sel, sd.lines[S.sel] + dir * step);
    draw();
    showLoupeDocked();
    commitSoon();
  }

  function onHandleKey(e) {
    const key = e.currentTarget.dataset.key, m = META[key];
    const step = e.shiftKey ? 5 : e.altKey ? 0.1 : 0.5;
    let dir = 0;
    if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') dir = -1;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') dir = 1;
    if (e.key === 'PageUp') dir = -10;
    if (e.key === 'PageDown') dir = 10;
    if (!dir) return;
    e.preventDefault();
    select(key);
    setLine(key, cur().lines[key] + dir * step);
    draw();
    showLoupeDocked();
    commitSoon();
    void m;
  }

  /* ==================================================================
     Pointer input: line drags, pan, pinch, double-tap zoom, ctrl+wheel
     ================================================================== */
  const ptrs = new Map();
  let mode = null;
  let lastTap = { t: 0, x: 0, y: 0 };

  function stagePt(e) {
    const r = el.stage.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  }

  function hitLine(p, touch) {
    const sd = cur(), L = sd.lines, v = S.view;
    const tol = touch ? 18 : 8;
    const X = (q) => v.tx + q * v.s, Y = (q) => v.ty + q * v.s;
    let best = null, bd = Infinity;
    for (const key of KEYS) {
      const m = META[key];
      let d, inSpan;
      if (m.axis === 'x') { d = Math.abs(p.x - X(L[key])); inSpan = p.y > Y(L.ot) - 40 && p.y < Y(L.ob) + 40; }
      else { d = Math.abs(p.y - Y(L[key])); inSpan = p.x > X(L.ol) - 40 && p.x < X(L.or) + 40; }
      // inner lines win ties so a thin border can still be grabbed
      if (inSpan && d <= tol && d + (m.kind === 'inner' ? -0.5 : 0) < bd) { bd = d; best = key; }
    }
    return best;
  }

  function onDown(e) {
    if (!cur().base) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    const p = stagePt(e);
    ptrs.set(e.pointerId, p);
    try { el.stage.setPointerCapture(e.pointerId); } catch (err) { /* ignore */ }

    if (ptrs.size === 2) {
      const [a, b] = [...ptrs.values()];
      hideLoupe();
      mode = { type: 'pinch', d0: Math.hypot(a.x - b.x, a.y - b.y) || 1, m0: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }, v0: { ...S.view } };
      return;
    }
    if (ptrs.size > 2) return;

    const h = e.target.closest('.cc-h');
    const key = h ? h.dataset.key : hitLine(p, e.pointerType !== 'mouse');
    if (key) {
      e.preventDefault();
      select(key);
      mode = { type: 'line', key, id: e.pointerId, p0: p, v0: cur().lines[key], moved: false, touch: e.pointerType !== 'mouse' };
      el.stage.classList.add('is-dragging');
      return;
    }
    mode = { type: 'pan', id: e.pointerId, p0: p, v0: { ...S.view }, moved: false };
  }

  function onMove(e) {
    const p = stagePt(e);
    if (!ptrs.has(e.pointerId)) {
      // hover cursor for mouse
      if (e.pointerType === 'mouse' && cur().base && !mode) {
        const key = hitLine(p, false);
        el.stage.style.cursor = key ? (META[key].axis === 'x' ? 'ew-resize' : 'ns-resize') : 'grab';
      }
      return;
    }
    ptrs.set(e.pointerId, p);
    if (!mode) return;
    if (mode.type === 'pinch' && ptrs.size >= 2) {
      const [a, b] = [...ptrs.values()];
      const d = Math.hypot(a.x - b.x, a.y - b.y) || 1;
      const m = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      const v0 = mode.v0, [lo, hi] = zoomLimits(cur());
      const s = clamp(v0.s * (d / mode.d0), lo, hi);
      S.view = { s, tx: m.x - (mode.m0.x - v0.tx) * (s / v0.s), ty: m.y - (mode.m0.y - v0.ty) * (s / v0.s), fitted: false };
      draw();
      return;
    }
    if (e.pointerId !== mode.id) return;
    const dx = p.x - mode.p0.x, dy = p.y - mode.p0.y;
    if (Math.hypot(dx, dy) > 3) mode.moved = true;
    if (mode.type === 'line') {
      const m = META[mode.key];
      const delta = (m.axis === 'x' ? dx : dy) / S.view.s;
      setLine(mode.key, mode.v0 + delta);
      draw();
      showLoupe(mode.key, p, mode.touch);
    } else if (mode.type === 'pan' && mode.moved) {
      S.view = { ...mode.v0, tx: mode.v0.tx + dx, ty: mode.v0.ty + dy, fitted: false };
      el.stage.style.cursor = 'grabbing';
      draw();
    }
  }

  function onUp(e) {
    const p = ptrs.get(e.pointerId);
    ptrs.delete(e.pointerId);
    if (!mode) return;
    if (mode.type === 'pinch') {
      if (ptrs.size < 2) mode = null;
      return;
    }
    if (e.pointerId !== mode.id) return;
    const wasTap = !mode.moved && e.type === 'pointerup';
    if (mode.type === 'line') {
      el.stage.classList.remove('is-dragging');
      hideLoupe();
      commit();
    } else if (wasTap && p) {
      const now = performance.now();
      if (now - lastTap.t < 320 && Math.hypot(p.x - lastTap.x, p.y - lastTap.y) < 30) {
        if (S.view.fitted) zoomAt(2.5, p.x, p.y); else fit();
        lastTap.t = 0;
      } else {
        lastTap = { t: now, x: p.x, y: p.y };
      }
    }
    el.stage.style.cursor = '';
    mode = null;
  }

  function onWheel(e) {
    if (!cur().base) return;
    if (!(e.ctrlKey || e.metaKey) && !el.dialog.open) {
      toast('Hold Ctrl (or ⌘) and scroll to zoom', 1600);
      return;
    }
    e.preventDefault();
    const p = stagePt(e);
    zoomAt(Math.exp(-e.deltaY * (e.ctrlKey && Math.abs(e.deltaY) < 30 ? 0.02 : 0.0025)), p.x, p.y);
  }

  /* ---------- loupe ---------- */
  function loupeSize() { return S.W < 560 ? 128 : 156; }

  function paintLoupe(key, cx, cy) {
    const sd = cur(), size = loupeSize(), d = S.dpr;
    if (el.loupe.width !== size * d) { el.loupe.width = size * d; el.loupe.height = size * d; }
    el.loupe.style.width = el.loupe.style.height = `${size}px`;
    const L = clamp(S.view.s * 4, 1.2, 14);
    const tx = size / 2 - cx * L, ty = size / 2 - cy * L;
    lctx.setTransform(d, 0, 0, d, 0, 0);
    lctx.fillStyle = '#05070a';
    lctx.fillRect(0, 0, size, size);
    drawImageTo(lctx, sd, L, tx, ty);
    drawOverlay(lctx, sd.lines, L, tx, ty, { bands: false, guides: true, labels: false, sel: key, w: size, h: size, lw: 1.25 });
  }

  function showLoupe(key, p, touch) {
    const sd = cur(), m = META[key], v = S.view;
    const size = loupeSize();
    const along = m.axis === 'x' ? (p.y - v.ty) / v.s : (p.x - v.tx) / v.s;
    const cx = m.axis === 'x' ? sd.lines[key] : along;
    const cy = m.axis === 'x' ? along : sd.lines[key];
    paintLoupe(key, cx, cy);
    let lx, ly;
    if (touch) {
      lx = p.x - size / 2;
      ly = p.y - size - 64;
      if (ly < 8) ly = p.y + 64;
    } else {
      lx = p.x + 26; ly = p.y - size - 26;
      if (ly < 8) ly = p.y + 26;
      if (lx + size > S.W - 8) lx = p.x - size - 26;
    }
    lx = clamp(lx, 8, S.W - size - 8);
    ly = clamp(ly, 8, S.H - size - 8);
    el.loupe.style.transform = `translate(${lx}px, ${ly}px)`;
    el.loupe.hidden = false;
  }

  let dockTimer = 0;
  function showLoupeDocked() {
    const sd = cur();
    if (!sd.base) return;
    const key = S.sel, m = META[key], L = sd.lines;
    const cx = m.axis === 'x' ? L[key] : L.ol + (L.or - L.ol) * (m.kind === 'outer' ? 0.34 : 0.66);
    const cy = m.axis === 'x' ? L.ot + (L.ob - L.ot) * (m.kind === 'outer' ? 0.34 : 0.66) : L[key];
    paintLoupe(key, cx, cy);
    el.loupe.style.transform = 'translate(10px, 10px)';
    el.loupe.hidden = false;
    clearTimeout(dockTimer);
    dockTimer = setTimeout(hideLoupe, 1400);
  }

  function hideLoupe() { el.loupe.hidden = true; }

  /* ==================================================================
     Measurement + grades
     ================================================================== */
  function pair(a, b) {
    const s = a + b;
    const pa = s > 0 ? (100 * a) / s : 50;
    return { a: pa, b: 100 - pa, big: Math.max(pa, 100 - pa) };
  }

  function measure(sd) {
    const L = sd.lines;
    if (!L) return null;
    const l = L.il - L.ol, r = L.or - L.ir, t = L.it - L.ot, b = L.ob - L.ib;
    const lr = pair(l, r), tb = pair(t, b);
    const mmX = S.size[0] / (L.or - L.ol), mmY = S.size[1] / (L.ob - L.ot);
    // placing a line is good to about a pixel, and each border has two lines
    const err = Math.max(106 / Math.max(l + r, 1), 106 / Math.max(t + b, 1));
    return { l, r, t, b, lr, tb, worst: Math.max(lr.big, tb.big), mm: { l: l * mmX, r: r * mmX, t: t * mmY, b: b * mmY }, err };
  }

  function gradeFor(g, F, B) {
    for (let i = 0; i < g.grades.length; i++) {
      const gr = g.grades[i];
      const okF = F == null || gr.f == null || F <= gr.f + 1e-9;
      const okB = B == null || gr.b == null || B <= gr.b + 1e-9;
      if (okF && okB) return { i, gr, next: i > 0 ? g.grades[i - 1] : null };
    }
    return { i: -1, gr: null, next: g.grades[g.grades.length - 1] };
  }

  function updateReadout() {
    const m = measure(cur());
    if (!m) return;
    const set = (k, txt) => $$(`[data-cc-read="${k}"]`).forEach((n) => { n.textContent = txt; });
    set('lr', `${f1(m.lr.a)} / ${f1(m.lr.b)}`);
    set('tb', `${f1(m.tb.a)} / ${f1(m.tb.b)}`);
    set('worst', ratio(m.worst));
    set('mm-l', `${m.mm.l.toFixed(2)} mm`); set('mm-r', `${m.mm.r.toFixed(2)} mm`);
    set('mm-t', `${m.mm.t.toFixed(2)} mm`); set('mm-b', `${m.mm.b.toFixed(2)} mm`);
    $$('[data-cc-gauge="lr"]').forEach((n) => n.style.setProperty('--p', f1(m.lr.a)));
    $$('[data-cc-gauge="tb"]').forEach((n) => n.style.setProperty('--p', f1(m.tb.a)));
    renderLive(m);
  }

  const liveList = $('[data-cc-live]'), liveNote = $('[data-cc-live-note]');
  function renderLive(m) {
    if (!liveList) return;
    const other = S.side === 'front' ? 'back' : 'front';
    const om = S.sides[other].base ? measure(S.sides[other]) : null;
    const F = S.side === 'front' ? m.worst : om && om.worst;
    const B = S.side === 'back' ? m.worst : om && om.worst;
    liveList.innerHTML = GRADERS.map((g) => {
      const r = gradeFor(g, F ?? null, B ?? null);
      return `<li class="${r.i === 0 ? 'is-top' : ''}" style="--g:${g.color}"><span>${g.name}</span><b>${r.gr ? r.gr.g : '<' + g.grades[g.grades.length - 1].g}</b></li>`;
    }).join('');
    if (liveNote) liveNote.textContent = F != null && B != null ? '(front + back)' : F != null ? '(front only so far)' : '(back only so far)';
  }

  let commitTimer = 0;
  function commitSoon() { clearTimeout(commitTimer); commitTimer = setTimeout(commit, 250); }

  function commit() {
    clearTimeout(commitTimer);
    renderResults();
  }

  function sideMeasures() {
    const out = {};
    for (const k of Object.keys(SIDES)) {
      const sd = S.sides[k];
      out[k] = sd.base ? measure(sd) : null;
    }
    return out;
  }

  function renderResults() {
    const M = sideMeasures();
    const any = M.front || M.back;
    el.results.hidden = !any;
    root.classList.toggle('cc--measured', !!any);
    if (!any) return;

    for (const k of Object.keys(SIDES)) {
      const card = $(`[data-cc-sidecard="${k}"]`);
      const m = M[k];
      card.classList.toggle('is-empty', !m);
      if (!m) continue;
      const q = (f) => card.querySelector(`[data-f="${f}"]`);
      q('lr').textContent = `${f1(m.lr.a)} / ${f1(m.lr.b)}`;
      q('tb').textContent = `${f1(m.tb.a)} / ${f1(m.tb.b)}`;
      q('worst').textContent = ratio(m.worst);
      q('err').textContent = m.err >= 0.4 ? `± ${m.err.toFixed(1)}` : '';
      q('mm').textContent = `L ${m.mm.l.toFixed(2)} · R ${m.mm.r.toFixed(2)} · T ${m.mm.t.toFixed(2)} · B ${m.mm.b.toFixed(2)} mm`;
      drawThumb(card.querySelector('[data-cc-thumb]'), S.sides[k]);
    }

    const F = M.front ? M.front.worst : null;
    const B = M.back ? M.back.worst : null;
    el.grades.innerHTML = '';
    for (const g of GRADERS) {
      const r = gradeFor(g, F, B);
      const top = r.i === 0;
      const li = document.createElement('li');
      li.className = 'cc-grader' + (top ? ' is-top' : '');
      li.style.setProperty('--g', g.color);
      const num = r.gr ? r.gr.g : `< ${g.grades[g.grades.length - 1].g}`;
      const label = r.gr ? r.gr.label : 'Below the listed grades';
      let why;
      if (top) {
        why = `<b class="cc-grader__ok">Centering is good enough for ${g.name}'s top grade.</b>`;
        const tight = (F != null && r.gr.f != null && r.gr.f - F < 1) || (B != null && r.gr.b != null && r.gr.b - B < 1);
        if (tight) why += ' Only just, so double-check your lines.';
      } else {
        const nx = r.next, bits = [];
        if (F != null && nx.f != null && F > nx.f) bits.push(`the front were ${limit(nx.f)} or better (yours: ${ratio(F)})`);
        if (B != null && nx.b != null && B > nx.b) bits.push(`the back were ${limit(nx.b)} or better (yours: ${ratio(B)})`);
        why = `Would be ${/^(8|11|18)/.test(nx.g) ? 'an' : 'a'} <b>${nx.g}${nx.label ? ' ' + nx.label : ''}</b> if ${bits.join(' and ') || 'the centering were better'}.`;
      }
      const pend = [];
      if (F == null) pend.push('Front not measured yet.');
      if (B == null) pend.push(g.short === 'front only' ? '' : 'Back not measured yet: it can lower this.');
      if (g.short === 'front only') pend.push('SGC only publishes front limits.');
      li.innerHTML = `
        <span class="cc-grader__badge">${g.name}</span>
        <p class="cc-grader__grade"><span class="cc-grader__num">${num}</span><span class="cc-grader__label">${label}</span></p>
        <p class="cc-grader__why">${why}${pend.filter(Boolean).length ? `<span class="cc-grader__pending">${pend.filter(Boolean).join(' ')}</span>` : ''}</p>`;
      el.grades.appendChild(li);
    }

    const parts = [];
    if (M.front) parts.push(`front ${ratio(M.front.worst)}`);
    if (M.back) parts.push(`back ${ratio(M.back.worst)}`);
    el.summary.textContent = `Worst split: ${parts.join(', ')}.` + (M.back ? '' : ' Measure the back too, it often decides the grade.');
    announce(el.summary.textContent);

    $$('[data-cc-act="next"]').forEach((b) => {
      const other = S.side === 'front' ? 'back' : 'front';
      b.textContent = S.sides[other].base ? `Switch to ${other}` : `Measure the ${other}`;
      b.dataset.target = other;
    });
    $$('[data-cc-side]').forEach((t) => t.classList.toggle('is-done', !!S.sides[t.dataset.ccSide].base));
  }

  function cropRect(sd, pad) {
    const L = sd.lines, w = L.or - L.ol, h = L.ob - L.ot;
    return { x: L.ol - w * pad, y: L.ot - h * pad, w: w * (1 + pad * 2), h: h * (1 + pad * 2) };
  }

  function drawCrop(c, sd, dx, dy, dw, dh, opts = {}) {
    const r = cropRect(sd, 0.05);
    const s = Math.min(dw / r.w, dh / r.h);
    const tx = dx + (dw - r.w * s) / 2 - r.x * s;
    const ty = dy + (dh - r.h * s) / 2 - r.y * s;
    c.save();
    c.beginPath(); c.rect(dx, dy, dw, dh); c.clip();
    c.fillStyle = '#0b0e12'; c.fillRect(dx, dy, dw, dh);
    drawImageTo(c, sd, s, tx, ty);
    drawOverlay(c, sd.lines, s, tx, ty, { bands: true, guides: false, labels: !!opts.labels, lw: opts.lw || 1.5 });
    c.restore();
  }

  function drawThumb(cv, sd) {
    if (!cv || !sd.base) return;
    const d = Math.min(window.devicePixelRatio || 1, 2);
    const w = cv.clientWidth || 132, h = cv.clientHeight || 184;
    cv.width = Math.round(w * d); cv.height = Math.round(h * d);
    const c = cv.getContext('2d');
    c.setTransform(d, 0, 0, d, 0, 0);
    drawCrop(c, sd, 0, 0, w, h, { lw: 1.25 });
  }

  /* ==================================================================
     Auto-detect
     ================================================================== */
  let scratchCv = null;
  function renderSmall(sd, angle, maxDim) {
    const b = sd.base, k = Math.min(1, maxDim / Math.max(b.width, b.height));
    const w = Math.round(b.width * k), h = Math.round(b.height * k);
    if (!scratchCv) scratchCv = document.createElement('canvas');
    scratchCv.width = w; scratchCv.height = h;
    const x = scratchCv.getContext('2d', { willReadFrequently: true });
    x.setTransform(1, 0, 0, 1, 0, 0);
    x.fillStyle = '#000'; x.fillRect(0, 0, w, h);
    x.translate(w / 2, h / 2);
    x.rotate((angle * Math.PI) / 180);
    x.drawImage(b, -w / 2, -h / 2, w, h);
    return { d: x.getImageData(0, 0, w, h).data, w, h, k };
  }

  function gradients(img) {
    const { d, w, h } = img, n = w * h;
    const gx = new Float32Array(n), gy = new Float32Array(n);
    const row = w * 4;
    for (let y = 1; y < h - 1; y++) {
      for (let x = 1; x < w - 1; x++) {
        const i = (y * w + x) * 4;
        const a = i + 4, b = i - 4, u = i - row, v = i + row;
        gx[y * w + x] = Math.max(Math.abs(d[a] - d[b]), Math.abs(d[a + 1] - d[b + 1]), Math.abs(d[a + 2] - d[b + 2]));
        gy[y * w + x] = Math.max(Math.abs(d[v] - d[u]), Math.abs(d[v + 1] - d[u + 1]), Math.abs(d[v + 2] - d[u + 2]));
      }
    }
    return { gx, gy, w, h };
  }

  function profiles(g, x0, x1, y0, y1) {
    const { gx, gy, w, h } = g;
    const col = new Float32Array(w), row = new Float32Array(h);
    x0 = Math.max(1, Math.round(x0)); x1 = Math.min(w - 1, Math.round(x1));
    y0 = Math.max(1, Math.round(y0)); y1 = Math.min(h - 1, Math.round(y1));
    for (let y = y0; y < y1; y++) { const o = y * w; for (let x = 1; x < w - 1; x++) col[x] += gx[o + x]; }
    for (let y = 1; y < h - 1; y++) { const o = y * w; let s = 0; for (let x = x0; x < x1; x++) s += gy[o + x]; row[y] = s; }
    return { col: smooth(col), row: smooth(row) };
  }

  function smooth(p) {
    const o = new Float32Array(p.length);
    for (let i = 1; i < p.length - 1; i++) o[i] = (p[i - 1] + 2 * p[i] + p[i + 1]) / 4;
    return o;
  }

  function sharpness(p) {
    let s = 0, s2 = 0;
    for (let i = 0; i < p.length; i++) { s += p[i]; s2 += p[i] * p[i]; }
    return s > 0 ? (s2 * p.length) / (s * s) : 0;
  }

  function peaks(p, a, b) {
    const out = [];
    a = Math.max(1, Math.floor(a)); b = Math.min(p.length - 2, Math.ceil(b));
    for (let i = a; i <= b; i++) if (p[i] > p[i - 1] && p[i] >= p[i + 1]) out.push(i);
    return out;
  }

  function subpx(p, i) {
    const a = p[i - 1] || 0, b = p[i], c = p[i + 1] || 0, den = a - 2 * b + c;
    return den !== 0 ? i + clamp((0.5 * (a - c)) / den, -0.5, 0.5) : i;
  }

  function maxIn(p, a, b) {
    a = Math.max(1, Math.floor(a)); b = Math.min(p.length - 2, Math.ceil(b));
    let bi = a;
    for (let i = a; i <= b; i++) if (p[i] > p[bi]) bi = i;
    return bi;
  }

  async function straightenAngle(sd) {
    const score = (ang, dim) => {
      const g = gradients(renderSmall(sd, ang, dim));
      const P = profiles(g, g.w * 0.08, g.w * 0.92, g.h * 0.08, g.h * 0.92);
      return sharpness(P.col) + sharpness(P.row);
    };
    let best = 0, bs = -1;
    for (let a = -8; a <= 8.001; a += 0.5) {
      const s = score(a, 420);
      if (s > bs) { bs = s; best = a; }
      if (Math.round(a * 2) % 8 === 0) await nextFrame();
    }
    let fine = best; bs = -1;
    for (let a = best - 0.5; a <= best + 0.501; a += 0.1) {
      const s = score(a, 720);
      if (s > bs) { bs = s; fine = a; }
    }
    return Math.round(fine * 10) / 10;
  }

  /* colour helpers on the detection image */
  function px(img, x, y) {
    const i = (Math.round(y) * img.w + Math.round(x)) * 4;
    return [img.d[i], img.d[i + 1], img.d[i + 2]];
  }
  const cdist = (a, b) => Math.max(Math.abs(a[0] - b[0]), Math.abs(a[1] - b[1]), Math.abs(a[2] - b[2]));
  function median3(list) {
    const m = [0, 1, 2].map((c) => list.map((p) => p[c]).sort((x, y) => x - y)[list.length >> 1]);
    return m;
  }
  // pixels of a line (column when axis 'x') at position k, between a0 and a1 on the other axis
  function lineSamples(img, axis, k, a0, a1, step) {
    const out = [];
    for (let t = a0; t <= a1; t += step) out.push(axis === 'x' ? px(img, k, t) : px(img, t, k));
    return out;
  }

  /* A tight scan: the card runs to the photo's edges, so its rounded corners
     show background in the photo's corners while the band just inside every
     edge is the (uniform) card border. */
  function scanBounds(img) {
    const { w, h } = img, target = S.size[0] / S.size[1];
    if (Math.abs(w / h / target - 1) > 0.045) return null;
    const bands = [
      lineSamples(img, 'x', w * 0.008, h * 0.2, h * 0.8, 3), lineSamples(img, 'x', w * 0.992, h * 0.2, h * 0.8, 3),
      lineSamples(img, 'y', h * 0.006, w * 0.2, w * 0.8, 3), lineSamples(img, 'y', h * 0.994, w * 0.2, w * 0.8, 3),
    ];
    const meds = bands.map(median3);
    for (let i = 0; i < 4; i++) {
      const frac = bands[i].filter((q) => cdist(q, meds[i]) < 34).length / bands[i].length;
      if (frac < 0.8) return null;
    }
    for (let i = 1; i < 4; i++) if (cdist(meds[i], meds[0]) > 48) return null;
    const cs = Math.max(2, Math.round(w * 0.006));
    const corners = [[cs, cs], [w - 1 - cs, cs], [cs, h - 1 - cs], [w - 1 - cs, h - 1 - cs]];
    const odd = corners.filter(([x, y]) => cdist(px(img, x, y), meds[0]) > 40).length;
    if (odd < 3) return null;
    return { ol: 0.5, or: w - 1.5, ot: 0.5, ob: h - 1.5 };
  }

  /* Card rectangle from edge-projection peaks, card aspect ratio as a
     constraint, biased hard towards the biggest rectangle whose four sides
     are all real edges (the border and art box make smaller ones). */
  function projectionBounds(g) {
    const { w, h } = g;
    let P = profiles(g, w * 0.2, w * 0.8, h * 0.2, h * 0.8);
    const target = S.size[0] / S.size[1];
    const cmax = Math.max(...P.col) || 1, rmax = Math.max(...P.row) || 1;
    // shortlist: the strongest peaks, plus the two outermost that clear 25% of the strongest.
    // Text lines inside a card can outrank its real edge (Mega Charizard X's bottom edge is
    // 0.55 against 0.66 to 1.0 for the attack text), so strength alone drops the true edge.
    const top = (p, a, b, n, mx, outerFirst) => {
      const pk = peaks(p, a, b);
      const strong = [...pk].sort((i, j) => p[j] - p[i]).slice(0, n);
      const outer = pk.filter((i) => p[i] >= mx * 0.25).sort((i, j) => (outerFirst ? i - j : j - i)).slice(0, 2);
      return [...new Set([...strong, ...outer])];
    };
    const Lc = top(P.col, 2, w * 0.5, 8, cmax, true), Rc = top(P.col, w * 0.5, w - 3, 8, cmax, false);
    const Tc = top(P.row, 2, h * 0.5, 8, rmax, true), Bc = top(P.row, h * 0.5, h - 3, 8, rmax, false);
    let best = null, bs = -Infinity;
    for (const L of Lc) for (const R of Rc) {
      const cw = R - L;
      if (cw < w * 0.18) continue;
      const sl = P.col[L] / cmax, sr = P.col[R] / cmax;
      if (Math.min(sl, sr) < 0.16) continue;
      for (const T of Tc) for (const B of Bc) {
        const ch = B - T;
        if (ch < h * 0.18) continue;
        const aErr = Math.abs(cw / ch / target - 1);
        if (aErr > 0.07) continue;
        const st = P.row[T] / rmax, sb = P.row[B] / rmax;
        if (Math.min(st, sb) < 0.16) continue;
        const sc = (cw / w + ch / h) * 3 + (sl + sr + st + sb) * 0.35 - aErr * 3;
        if (sc > bs) { bs = sc; best = { L, R, T, B }; }
      }
    }
    if (!best) return null;
    const cw = best.R - best.L, ch = best.B - best.T;
    P = profiles(g, best.L + cw * 0.14, best.R - cw * 0.14, best.T + ch * 0.12, best.B - ch * 0.12);
    const ref = (p, i, win) => subpx(p, maxIn(p, i - win, i + win));
    return {
      ol: ref(P.col, best.L, cw * 0.012), or: ref(P.col, best.R, cw * 0.012),
      ot: ref(P.row, best.T, ch * 0.01), ob: ref(P.row, best.B, ch * 0.01),
    };
  }

  /* Border line: a printed border is one flat colour along its whole length,
     the artwork inside it isn't. Take the median colour of each line parallel
     to the edge (medians shrug off text, set symbols and holo sparkle printed
     in the border), learn the border's colour just inside the card edge, then
     walk inward until the line colour leaves it. The line goes at the
     midpoint of that colour step. */
  function lineMedian(img, axis, k, a0, a1, step) {
    return median3(lineSamples(img, axis, k, a0, a1, step));
  }

  function uniformEdge(img, axis, from, dir, span, a0, a1) {
    const lim = axis === 'x' ? img.w - 2 : img.h - 2;
    const at = (k) => clamp(k, 1, lim);
    const step = Math.max(1, Math.round((a1 - a0) / 300));
    const s0 = from + dir * Math.max(1.5, span * 0.006), s1 = from + dir * Math.max(3, span * 0.012);
    const pix = [], meds = [];
    for (let k = s0; dir * (s1 - k) >= 0; k += dir) {
      const q = lineSamples(img, axis, at(k), a0, a1, step);
      pix.push(...q);
      meds.push(median3(q));
    }
    if (meds.length < 2) return null;
    const bm = median3(meds);
    const spread = Math.max(...meds.map((q) => cdist(q, bm)));
    if (spread > 40) return null;                        // no flat border here (full art)
    const thr = clamp(spread * 2.5 + 8, 14, 40);
    const pd = pix.map((q) => cdist(q, bm)).sort((x, y) => x - y);
    const tol = clamp(pd[Math.floor(pd.length * 0.85)] * 1.5 + 8, 16, 45);
    const base = pd.filter((d) => d <= tol).length / pd.length;
    // a line is past the border when its median colour has moved AND most of its
    // pixels stopped matching; text printed in the border moves the first only
    const probe = (k) => {
      const q = lineSamples(img, axis, at(k), a0, a1, step);
      const m = median3(q);
      const frac = q.filter((c) => cdist(c, bm) <= tol).length / q.length;
      return { d: cdist(m, bm), out: cdist(m, bm) > thr && frac / base < 0.6 };
    };
    const end = from + dir * span * 0.2;
    const hist = [];
    for (let k = s0; dir * (end - k) > 0; k += dir) {
      const p = probe(k);
      hist.push([k, p.d]);
      if (p.out && probe(k + dir).out) {
        const plateau = Math.max(p.d, probe(k + dir).d, probe(k + 2 * dir).d);
        const half = Math.max(thr * 0.75, plateau / 2);
        // the midpoint of the colour step, searched back from here only
        for (let i = hist.length - 1; i >= 1; i--) {
          if (hist[i - 1][1] < half && hist[i][1] >= half) {
            const [k0, d0] = hist[i - 1], [, d1] = hist[i];
            const t = d1 === d0 ? 0.5 : clamp((half - d0) / (d1 - d0), 0, 1);
            return k0 + dir * t;
          }
        }
        return k;
      }
    }
    return null;
  }

  function findCard(g, img) {
    const out = scanBounds(img) || projectionBounds(g);
    if (!out) return null;
    const cw = out.or - out.ol, ch = out.ob - out.ot;
    const P = profiles(g, out.ol + cw * 0.14, out.or - cw * 0.14, out.ot + ch * 0.12, out.ob - ch * 0.12);
    // gradient fallback: the first strong edge inward (full-art cards, no flat border)
    const inward = (p, from, dir, span) => {
      const a = from + dir * span * 0.012, b = from + dir * span * 0.17;
      const pk = peaks(p, Math.min(a, b), Math.max(a, b));
      if (!pk.length) return from + dir * span * 0.05;
      const mx = Math.max(...pk.map((i) => p[i]));
      const strong = pk.filter((i) => p[i] >= mx * 0.4).sort((i, j) => (i - j) * dir);
      return subpx(p, strong[0]);
    };
    const ya0 = out.ot + ch * 0.1, ya1 = out.ob - ch * 0.1;
    const xa0 = out.ol + cw * 0.1, xa1 = out.or - cw * 0.1;
    const flat = {
      il: uniformEdge(img, 'x', out.ol, 1, cw, ya0, ya1),
      ir: uniformEdge(img, 'x', out.or, -1, cw, ya0, ya1),
      it: uniformEdge(img, 'y', out.ot, 1, ch, xa0, xa1),
      ib: uniformEdge(img, 'y', out.ob, -1, ch, xa0, xa1),
    };
    out.il = flat.il ?? inward(P.col, out.ol, 1, cw);
    out.ir = flat.ir ?? inward(P.col, out.or, -1, cw);
    out.it = flat.it ?? inward(P.row, out.ot, 1, ch);
    out.ib = flat.ib ?? inward(P.row, out.ob, -1, ch);
    out.noFlat = Object.values(flat).filter((v) => v == null).length;
    return out;
  }

  async function autoDetect(quiet) {
    const sd = cur();
    if (!sd.base) return;
    showBusy('Finding the card edges');
    await nextFrame();
    try {
      const ang = await straightenAngle(sd);
      const img = renderSmall(sd, ang, 1100);
      let found = findCard(gradients(img), img);
      if (found && sd.guide) {
        const T = {};
        for (const k of KEYS) T[k] = found[k] / img.k;
        if (!matchesGuide(T, sd.guide)) found = null;   // locked onto something else; keep the guide lines
      }
      if (found) {
        sd.angle = ang;
        const L = {};
        for (const k of KEYS) L[k] = found[k] / img.k;  // noFlat is not a line key, so it stays behind
        sd.lines = L;
        el.angle.value = String(ang);
        el.angleVal.textContent = `${ang.toFixed(1)}°`;
        fit();
        commit();
        const m = measure(sd);
        const odd = (m.lr.big > 62 && m.tb.big > 62) || m.worst > 80;
        const touch = matchMedia('(pointer: coarse)').matches;
        if (odd) {
          toast('Those numbers look unusual. Check the orange lines sit on the card edges. A darker, plain surface helps.', 6000);
        } else if (found.noFlat >= 2) {
          toast("No flat border found on some sides (full art?). Put the blue lines on the printed frame you're measuring to.", 6000);
        } else {
          toast(touch ? 'Lines placed. Drag a handle to adjust, pinch to zoom.' : 'Lines placed. Check each one, then fine-tune.', 3200);
        }
      } else {
        if (sd.guide) {
          sd.lines = guideLines(sd.guide);
          fit();
          toast('Lines start on the guide frame. Drag the orange lines onto the card edges.', 4200);
        } else {
          toast("Couldn't find the card clearly. Drag the lines into place, or retake on a plain, darker surface.", 4200);
        }
        commit();
      }
    } catch (err) {
      toast('Auto-detect hit a problem. Drag the lines into place by hand.', 3600);
      commit();
    }
    hideBusy();
  }

  function showBusy(msg) {
    el.busy.querySelector('[data-cc-busy-msg]').textContent = msg;
    el.busy.hidden = false;
  }
  function hideBusy() { el.busy.hidden = true; }

  /* ==================================================================
     Workspace placement (inline on desktop, full-screen dialog on phones)
     ================================================================== */
  const mqModal = matchMedia('(max-width: 989px)');
  let ro = null;

  function lockScroll(on) { document.documentElement.classList.toggle('cc-lock', on); }

  function openWs() {
    el.ws.hidden = false;
    if (mqModal.matches) {
      if (el.ws.parentElement !== el.dialog) el.dialog.appendChild(el.ws);
      if (!el.dialog.open) el.dialog.showModal();
      lockScroll(true);
      root.classList.remove('cc--inline');
    } else {
      if (el.ws.parentElement !== el.host) el.host.appendChild(el.ws);
      el.host.hidden = false;
      root.classList.add('cc--inline');
      if (el.dialog.open) el.dialog.close();
      lockScroll(false);
      requestAnimationFrame(() => el.host.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' }));
    }
    if (!ro) {
      ro = new ResizeObserver(() => {
        if (!resizeCanvas()) return;
        if (S.view.fitted) fit(); else draw();
      });
      ro.observe(el.stage);
    }
    resizeCanvas();
  }

  function closeWs() {
    if (el.dialog.open) el.dialog.close();
    lockScroll(false);
    if (mqModal.matches) {
      el.host.appendChild(el.ws);
      el.ws.hidden = true;
      el.host.hidden = true;
      commit();
      requestAnimationFrame(() => el.results.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' }));
    }
  }

  mqModal.addEventListener('change', () => {
    if (el.ws.hidden) return;
    if (!mqModal.matches && el.dialog.open) { openWs(); fit(); }
  });
  el.dialog.addEventListener('cancel', (e) => { e.preventDefault(); closeWs(); });

  function setSide(k, keepView) {
    S.side = k;
    $$('[data-cc-side]').forEach((t) => {
      const on = t.dataset.ccSide === k;
      t.setAttribute('aria-pressed', on ? 'true' : 'false');
      t.classList.toggle('is-on', on);
    });
    const sd = cur();
    el.angle.value = String(sd.angle || 0);
    el.angleVal.textContent = `${(sd.angle || 0).toFixed(1)}°`;
    $$('[data-cc-side-name]').forEach((n) => { n.textContent = SIDES[k].toLowerCase(); });
    if (!keepView && sd.base) fit(); else draw();
    commit();
  }

  /* ==================================================================
     Camera
     ================================================================== */
  let stream = null, facing = 'environment';

  function stopStream() {
    if (stream) stream.getTracks().forEach((t) => t.stop());
    stream = null;
    el.video.srcObject = null;
  }

  async function startStream() {
    stopStream();
    el.camMsg.textContent = 'Starting the camera…';
    el.camMsg.hidden = false;
    stream = await navigator.mediaDevices.getUserMedia({
      audio: false,
      // ask for a frame shaped like the screen, so a phone held upright gets a tall picture
      // that fills the viewfinder instead of a letterboxed landscape strip
      video: matchMedia('(orientation: portrait)').matches
        ? { facingMode: { ideal: facing }, width: { ideal: 2160 }, height: { ideal: 3840 }, aspectRatio: { ideal: 9 / 16 } }
        : { facingMode: { ideal: facing }, width: { ideal: 3840 }, height: { ideal: 2160 } },
    });
    el.video.srcObject = stream;
    await el.video.play();
    el.camMsg.hidden = true;
    sizeGuide();
  }

  /* The viewfinder shows the WHOLE camera frame (object-fit: contain) so what you see is what
     gets measured. The card guide sits inside the visible video area at about 70% of its
     height, leaving a margin all round so the card's edges are never cut off. */
  function sizeGuide() {
    const view = el.video.parentElement, g = $('.cc-cam__guide', el.cam);
    if (!view || !g) return;
    const vw = el.video.videoWidth || 3, vh = el.video.videoHeight || 4;
    const bw = view.clientWidth, bh = view.clientHeight;
    const k = Math.min(bw / vw, bh / vh);
    const dw = vw * k, dh = vh * k;
    const asp = S.size[0] / S.size[1];
    let gh = dh * 0.7, gw = gh * asp;
    if (gw > dw * 0.8) { gw = dw * 0.8; gh = gw / asp; }
    g.style.width = gw + 'px';
    g.style.height = gh + 'px';
  }
  window.addEventListener('resize', () => { if (el.cam.open) sizeGuide(); });
  el.video.addEventListener('loadedmetadata', sizeGuide);

  async function openCamera() {
    if (!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia) || !window.isSecureContext) {
      openPicker(el.capture);
      return;
    }
    el.cam.showModal();
    sizeGuide();
    lockScroll(true);
    try {
      await startStream();
    } catch (err) {
      el.camMsg.textContent = err && err.name === 'NotAllowedError'
        ? 'Camera access was blocked. Allow it in your browser settings, or use your camera app below.'
        : "We couldn't start the camera here. Use your camera app instead.";
    }
  }

  function closeCamera() {
    stopStream();
    if (el.cam.open) el.cam.close();
    if (!el.dialog.open) lockScroll(false);
  }

  async function shoot() {
    if (!stream) return;
    root.classList.add('cc--flash');
    setTimeout(() => root.classList.remove('cc--flash'), 260);
    // Grab the video frame itself rather than ImageCapture.takePhoto(): a still can use a
    // different crop/zoom from the preview, and then the photo no longer matches the frame.
    const base = toBase(el.video);
    // where the guide frame sat in the photo: the video is shown object-fit: contain
    let guide = null;
    const gEl = $('.cc-cam__guide', el.cam), view = el.video.parentElement;
    const vw = el.video.videoWidth, vh = el.video.videoHeight;
    if (gEl && view && vw && vh) {
      const vr = view.getBoundingClientRect(), gr = gEl.getBoundingClientRect();
      const k = Math.min(vr.width / vw, vr.height / vh);
      const ox = vr.left + (vr.width - vw * k) / 2, oy = vr.top + (vr.height - vh * k) / 2;
      const kb = base.width / vw;
      guide = {
        x0: ((gr.left - ox) / k) * kb, y0: ((gr.top - oy) / k) * kb,
        x1: ((gr.right - ox) / k) * kb, y1: ((gr.bottom - oy) / k) * kb,
      };
      if (!(guide.x1 - guide.x0 > 10 && guide.y1 - guide.y0 > 10)) guide = null;
    }
    closeCamera();
    await setImage(base, 'Camera photo', guide);
  }

  el.cam.addEventListener('cancel', (e) => { e.preventDefault(); closeCamera(); });

  /* ==================================================================
     Share / save
     ================================================================== */
  // our logo (white lettering version, theme asset) for the export; preloaded so Save is instant
  const logoImg = new Image();
  if (root.dataset.ccLogo) { logoImg.crossOrigin = 'anonymous'; logoImg.src = root.dataset.ccLogo; }

  async function composeShare() {
    if (logoImg.src && !logoImg.complete) { try { await logoImg.decode(); } catch (err) { /* text fallback below */ } }
    const M = sideMeasures();
    const W = 1080, H = 1350;
    const c = document.createElement('canvas');
    c.width = W; c.height = H;
    const x = c.getContext('2d');
    x.fillStyle = '#0e1116'; x.fillRect(0, 0, W, H);
    const glow = x.createRadialGradient(W * 0.8, 0, 10, W * 0.8, 0, W);
    glow.addColorStop(0, 'rgba(232,181,74,0.18)'); glow.addColorStop(1, 'rgba(232,181,74,0)');
    x.fillStyle = glow; x.fillRect(0, 0, W, H);
    const sans = 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif';
    const mono = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';
    if (logoImg.complete && logoImg.naturalWidth) {
      const lh = 58, lw = lh * (logoImg.naturalWidth / logoImg.naturalHeight);
      x.drawImage(logoImg, 64, 34, lw, lh);
    } else {
      x.fillStyle = '#e8b54a'; x.font = `700 26px ${sans}`;
      x.fillText("COLLECTOR'S EDGE", 64, 84);
    }
    x.fillStyle = '#fff'; x.font = `800 60px ${sans}`;
    x.fillText('Card centering check', 64, 164);
    const sides = Object.keys(SIDES).filter((k) => M[k]);
    const colW = sides.length === 2 ? 452 : 560, gap = 48;
    const startX = sides.length === 2 ? 64 : (W - colW) / 2;
    sides.forEach((k, i) => {
      const x0 = startX + i * (colW + gap), y0 = 200, ih = 620;
      drawCrop(x, S.sides[k], x0, y0, colW, ih, { lw: 2.5 });
      const m = M[k];
      x.fillStyle = '#9aa3ad'; x.font = `600 24px ${sans}`;
      x.fillText(SIDES[k].toUpperCase(), x0, y0 + ih + 46);
      x.fillStyle = '#fff'; x.font = `700 34px ${mono}`;
      x.fillText(`L/R ${f1(m.lr.a)}/${f1(m.lr.b)}`, x0, y0 + ih + 92);
      x.fillText(`T/B ${f1(m.tb.a)}/${f1(m.tb.b)}`, x0, y0 + ih + 136);
    });
    const F = M.front ? M.front.worst : null, B = M.back ? M.back.worst : null;
    x.fillStyle = '#9aa3ad'; x.font = `600 24px ${sans}`;
    x.fillText('CENTERING GRADE', 64, 1060);
    let gx = 64, gy = 1108;
    x.font = `700 32px ${sans}`;
    for (const g of GRADERS) {
      const r = gradeFor(g, F, B);
      const txt = `${g.name} ${r.gr ? r.gr.g : '<' + g.grades[g.grades.length - 1].g}`;
      const w = x.measureText(txt).width + 36;
      if (gx + w > W - 64) { gx = 64; gy += 64; }
      x.fillStyle = g.color; x.beginPath();
      x.roundRect ? x.roundRect(gx, gy - 38, w, 52, 10) : x.rect(gx, gy - 38, w, 52); x.fill();
      x.fillStyle = '#fff'; x.fillText(txt, gx + 18, gy);
      gx += w + 14;
    }
    x.fillStyle = '#d0ac6b'; x.font = `600 24px ${sans}`;
    x.fillText('Grading with Ace? Code CE takes 5% off.', 64, H - 136);
    x.fillStyle = '#9aa3ad'; x.font = `500 22px ${sans}`;
    x.fillText(`${B == null || F == null ? 'One side measured. ' : ''}Centering only, not a grade prediction.`, 64, H - 92);
    x.fillStyle = '#fff'; x.font = `600 24px ${sans}`;
    x.fillText('collectorsedge.co.uk/centering-tool', 64, H - 54);
    return c;
  }

  async function shareResult(save) {
    const c = await composeShare();
    const blob = await new Promise((r) => c.toBlob(r, 'image/png'));
    if (!blob) return;
    const file = new File([blob], 'card-centering.png', { type: 'image/png' });
    if (!save && navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({ files: [file], title: 'Card centering check', text: 'Checked with the Collector\'s Edge centering tool' });
        return;
      } catch (err) {
        if (err && err.name === 'AbortError') return;
      }
    }
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'card-centering.png';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 4000);
  }

  /* ==================================================================
     Wiring
     ================================================================== */
  buildHandles();
  select('ol');
  // read-only hook for automated checks
  root.ccState = () => JSON.parse(JSON.stringify({ side: S.side, angle: cur().angle, lines: cur().lines, size: cur().base ? [cur().base.width, cur().base.height] : null }));
  if (!(navigator.canShare)) $$('[data-cc-act="share"]').forEach((b) => { b.hidden = true; });

  el.stage.addEventListener('pointerdown', onDown);
  el.stage.addEventListener('pointermove', onMove);
  el.stage.addEventListener('pointerup', onUp);
  el.stage.addEventListener('pointercancel', onUp);
  el.stage.addEventListener('lostpointercapture', (e) => { if (ptrs.has(e.pointerId)) onUp(e); });
  el.stage.addEventListener('wheel', onWheel, { passive: false });
  el.stage.addEventListener('contextmenu', (e) => { if (e.pointerType !== 'mouse') e.preventDefault(); });

  el.file.addEventListener('change', () => {
    const f = el.file.files[0];
    el.file.value = '';
    if (!f) return;
    if (el.cam.open) closeCamera();
    loadBlob(f);
  });
  el.capture.addEventListener('change', () => {
    const f = el.capture.files[0];
    el.capture.value = '';
    if (!f) return;
    if (el.cam.open) closeCamera();
    loadBlob(f, 'Camera photo');
  });

  el.angle.addEventListener('input', () => {
    const sd = cur();
    sd.angle = parseFloat(el.angle.value) || 0;
    el.angleVal.textContent = `${sd.angle.toFixed(1)}°`;
    draw();
  });
  el.angle.addEventListener('change', commit);

  el.size.addEventListener('change', () => {
    S.size = el.size.value.split('x').map(Number);
    draw(); commit();
  });

  // hold-to-repeat nudge buttons
  $$('[data-cc-nudge]').forEach((b) => {
    let t1 = 0, t2 = 0;
    const dir = Number(b.dataset.ccNudge);
    const stop = () => { clearTimeout(t1); clearInterval(t2); };
    b.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      nudge(dir, 0.5);
      let n = 0;
      t1 = setTimeout(() => { t2 = setInterval(() => { n++; nudge(dir, n > 20 ? 2 : 0.5); }, 55); }, 380);
    });
    ['pointerup', 'pointerleave', 'pointercancel'].forEach((ev) => b.addEventListener(ev, stop));
    b.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); nudge(dir, 0.5); } });
  });

  /* Everything outside an open modal <dialog> is inert, and iOS Safari ignores .click() on an
     inert file input (the "New" button did nothing). Move the input into whichever dialog is
     on top before opening it. */
  function openPicker(input) {
    const host = el.cam.open ? el.cam : el.dialog.open ? el.dialog : root;
    if (input.parentElement !== host) host.appendChild(input);
    input.click();
  }

  function pickUpload(side) {
    S.uploadSide = side;
    openPicker(el.file);
  }

  function startOver() {
    S.sides = { front: newSide(), back: newSide() };
    S.uploadSide = 'front';
    S.side = 'front';
  }

  root.addEventListener('click', async (e) => {
    const b = e.target.closest('[data-cc-act], [data-cc-side]');
    if (!b || !root.contains(b)) return;
    if (b.dataset.ccSide) {
      setSide(b.dataset.ccSide);
      if (el.ws.hidden) openWs();
      return;
    }
    const act = b.dataset.ccAct;
    const from = b.dataset.from;              // 'launch' = a new card
    switch (act) {
      case 'upload':
        if (from === 'launch') startOver();
        pickUpload(from === 'launch' ? 'front' : S.side);
        break;
      case 'camera':
        if (from === 'launch') startOver();
        S.uploadSide = from === 'launch' ? 'front' : S.side;
        openCamera();
        break;
      case 'sample': {
        if (from === 'launch') startOver();
        S.uploadSide = from === 'launch' ? 'front' : S.side;
        showBusy('Drawing a sample card');
        await nextFrame();
        await setImage(await samplePhoto(S.uploadSide === 'back'), 'Sample card');
        break;
      }
      case 'auto': autoDetect(false); break;
      case 'rotate': {
        const sd = cur();
        if (!sd.base) break;
        sd.base = rotateBase(sd.base, 1);
        sd.guide = null;
        sd.angle = 0;
        sd.lines = defaultLines(sd.base);
        fit();
        autoDetect(true);
        break;
      }
      case 'zoom-in': case 'zoom-out': {
        const p = handlePos(S.sel);
        const cx = p.vis ? p.x : S.W / 2, cy = p.vis ? p.y : S.H / 2;
        zoomAt(act === 'zoom-in' ? 1.6 : 1 / 1.6, cx, cy);
        break;
      }
      case 'fit': fit(); break;
      case 'angle-reset':
        cur().angle = 0; el.angle.value = '0'; el.angleVal.textContent = '0.0°'; draw(); commit();
        break;
      case 'straighten':
        root.classList.toggle('cc--show-angle');
        b.setAttribute('aria-expanded', root.classList.contains('cc--show-angle') ? 'true' : 'false');
        break;
      case 'selnext': {
        const i = KEYS.indexOf(S.sel);
        select(KEYS[(i + (b.dataset.dir === '-1' ? KEYS.length - 1 : 1)) % KEYS.length]);
        showLoupeDocked();
        break;
      }
      case 'reset-lines': {
        const sd = cur();
        if (sd.base) { sd.lines = defaultLines(sd.base); fit(); commit(); }
        break;
      }
      case 'next': {
        const other = b.dataset.target || (S.side === 'front' ? 'back' : 'front');
        setSide(other);
        if (el.ws.hidden) openWs();
        break;
      }
      case 'edit':
        setSide(b.dataset.side || 'front');
        openWs();
        fit();
        break;
      case 'done': closeWs(); break;
      case 'save': shareResult(true); break;
      case 'share': shareResult(false); break;
      case 'copy': {
        const code = b.dataset.code || 'CE';
        try { await navigator.clipboard.writeText(code); } catch (err) { /* older browsers: the code is on screen anyway */ }
        const label = b.querySelector('[data-cc-copy-label]') || b;
        const old = label.textContent;
        label.textContent = 'Copied';
        b.classList.add('is-copied');
        setTimeout(() => { label.textContent = old; b.classList.remove('is-copied'); }, 1800);
        break;
      }
      case 'cam-close': closeCamera(); break;
      case 'cam-shoot': shoot(); break;
      case 'cam-flip':
        facing = facing === 'environment' ? 'user' : 'environment';
        try { await startStream(); } catch (err) { /* keep the old stream message */ }
        break;
      case 'cam-native': openPicker(el.capture); break;
      case 'cam-library': openPicker(el.file); break;
      default: break;
    }
  });

  // drag and drop, anywhere on the tool
  ['dragenter', 'dragover'].forEach((ev) => root.addEventListener(ev, (e) => {
    if (!e.dataTransfer || ![...e.dataTransfer.types].includes('Files')) return;
    e.preventDefault();
    root.classList.add('cc--dragover');
  }));
  ['dragleave', 'drop'].forEach((ev) => root.addEventListener(ev, (e) => {
    if (ev === 'dragleave' && root.contains(e.relatedTarget)) return;
    root.classList.remove('cc--dragover');
  }));
  root.addEventListener('drop', (e) => {
    const f = e.dataTransfer && e.dataTransfer.files[0];
    if (!f) return;
    e.preventDefault();
    const inWs = el.ws.contains(e.target) && !el.ws.hidden;
    if (!inWs) startOver();
    S.uploadSide = inWs ? S.side : 'front';
    loadBlob(f);
  });

  // paste a screenshot straight in
  document.addEventListener('paste', (e) => {
    const item = [...(e.clipboardData ? e.clipboardData.items : [])].find((i) => i.type.startsWith('image/'));
    if (!item) return;
    const inWs = !el.ws.hidden;
    if (!inWs) startOver();
    S.uploadSide = inWs ? S.side : 'front';
    loadBlob(item.getAsFile(), 'Pasted image');
  });

  /* ---------- manual calculator (ruler measurements) ---------- */
  const calcOut = $('[data-cc-calc-out]');
  function calcSide(k) {
    const box = $(`[data-cc-calc="${k}"]`);
    if (!box) return null;
    const v = {};
    box.querySelectorAll('input').forEach((i) => {
      const n = parseFloat(String(i.value).replace(',', '.'));
      v[i.dataset.edge] = Number.isFinite(n) && n >= 0 ? n : null;
    });
    const lr = v.left != null && v.right != null && v.left + v.right > 0 ? pair(v.left, v.right) : null;
    const tb = v.top != null && v.bottom != null && v.top + v.bottom > 0 ? pair(v.top, v.bottom) : null;
    if (!lr && !tb) return null;
    return { lr, tb, worst: Math.max(lr ? lr.big : 0, tb ? tb.big : 0) };
  }
  function renderCalc() {
    if (!calcOut) return;
    const F = calcSide('front'), B = calcSide('back');
    if (!F && !B) {
      calcOut.innerHTML = '<p class="cc-calc__empty">Enter a left and right border (and top and bottom) to see the split and the grades it allows.</p>';
      return;
    }
    const row = (label, m) => {
      if (!m) return '';
      const bits = [];
      if (m.lr) bits.push(`<span>Left/right <b>${f1(m.lr.a)}/${f1(m.lr.b)}</b></span>`);
      if (m.tb) bits.push(`<span>Top/bottom <b>${f1(m.tb.a)}/${f1(m.tb.b)}</b></span>`);
      return `<p class="cc-calc__row"><strong>${label}</strong>${bits.join('')}<span>Worst <b>${ratio(m.worst)}</b></span></p>`;
    };
    const grades = GRADERS.map((g) => {
      const r = gradeFor(g, F ? F.worst : null, B ? B.worst : null);
      return `<li style="--g:${g.color}"><span>${g.name}</span><b>${r.gr ? r.gr.g : '< ' + g.grades[g.grades.length - 1].g}</b></li>`;
    }).join('');
    calcOut.innerHTML = `${row('Front', F)}${row('Back', B)}
      <p class="cc-calc__lbl">Centering alone allows up to${F && B ? '' : ' (one side entered)'}</p>
      <ul class="cc-calc__grades">${grades}</ul>`;
  }
  $$('[data-cc-calc] input').forEach((i) => i.addEventListener('input', renderCalc));

  /* grading-company tabs (phones only; CSS ignores is-off on wider screens) */
  $$('[data-std-tab]').forEach((t) => t.addEventListener('click', () => {
    $$('[data-std-tab]').forEach((x) => {
      const on = x === t;
      x.classList.toggle('is-on', on);
      x.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    $$('[data-std]').forEach((d) => d.classList.toggle('is-off', d.dataset.std !== t.dataset.stdTab));
  }));

  // a link to #centering-calculator opens the collapsed calculator
  const calcBox = $('#centering-calculator');
  if (calcBox && location.hash === '#centering-calculator') calcBox.open = true;

  // keyboard shortcuts inside the workspace
  el.ws.addEventListener('keydown', (e) => {
    if (e.target.matches('input, select, textarea')) return;
    if (e.target.classList.contains('cc-h')) return;
    if (e.key === '+' || e.key === '=') { zoomAt(1.4, S.W / 2, S.H / 2); e.preventDefault(); }
    else if (e.key === '-') { zoomAt(1 / 1.4, S.W / 2, S.H / 2); e.preventDefault(); }
    else if (e.key === '0') { fit(); e.preventDefault(); }
  });
})();
